// Gestão DNA · Ocorrências pedagógicas (visão geral por aluno/tipo/gravidade).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, Plus, Search, X } from 'lucide-react';
import api from '@/lib/api';
import { usePermV2 } from '@/lib/perm_v2';
import { GRAVIDADES, OcorrenciaForm, OcorrenciaItem, TIPOS } from './OcorrenciasPanel';

export default function GestaoOcorrencias() {
  const { can, perms } = usePermV2();
  const isAdmin = perms?.is_admin_bypass === true;
  const podeCriar = isAdmin || can('pedagogico', 'criar');
  const podeEditar = isAdmin || can('pedagogico', 'editar');
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState({});
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState('');
  const [gravidade, setGravidade] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [alunoQ, setAlunoQ] = useState('');
  const [alunos, setAlunos] = useState([]);
  const [aluno, setAluno] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pedagogico/ocorrencias', {
        params: { tipo: tipo || undefined, gravidade: gravidade || undefined, limit: 200 },
      });
      setItems(data.items || []);
      setResumo(data.resumo || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar ocorrências');
    } finally { setLoading(false); }
  }, [tipo, gravidade]);

  useEffect(() => { load(); }, [load]);

  const buscarAluno = async (term) => {
    setAlunoQ(term);
    if (term.trim().length < 2) { setAlunos([]); return; }
    try {
      const { data } = await api.get('/v2/aluno/search', { params: { q: term, limit: 8 } });
      setAlunos(Array.isArray(data) ? data : (data?.items || []));
    } catch (_) { setAlunos([]); }
  };

  const salvar = async () => {
    if (!form?.id && !aluno) { toast.error('Selecione o aluno.'); return; }
    if (!(form?.titulo || '').trim()) { toast.error('Informe o título da ocorrência.'); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/pedagogico/ocorrencias/${form.id}`, form);
      else await api.post('/pedagogico/ocorrencias', { ...form, student_id: aluno.id });
      toast.success(form.id ? 'Ocorrência atualizada' : 'Ocorrência registrada');
      setForm(null); setAluno(null); setAlunoQ(''); setAlunos([]); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar ocorrência');
    } finally { setSaving(false); }
  };

  const excluir = async (o) => {
    try {
      await api.delete(`/pedagogico/ocorrencias/${o.id}`);
      toast.success('Ocorrência excluída'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao excluir'); }
  };

  const chip = (ativo) => `rounded-full px-3.5 py-1.5 text-xs font-bold transition-all ${ativo
    ? 'bg-violet-600 text-white shadow-md shadow-violet-600/20'
    : 'border border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-700'}`;

  return (
    <div className="mx-auto max-w-[1280px] space-y-6" data-testid="gestao-ocorrencias">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-violet-600">Acompanhamento pedagógico</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Ocorrências</h1>
          <p className="mt-2 max-w-xl text-base text-slate-500">
            Registro interno de indisciplina, saúde, atrasos, elogios e comunicações. O histórico
            completo também aparece na ficha de cada aluno.
          </p>
        </div>
        {podeCriar && (
          <button onClick={() => setForm({ tipo: 'pedagogico', gravidade: 'leve', data: new Date().toISOString().slice(0, 10) })}
            className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition-all hover:-translate-y-0.5 hover:bg-violet-500"
            data-testid="ocorrencias-nova">
            <Plus className="h-4 w-4" /> Nova ocorrência
          </button>
        )}
      </header>

      <div className="grid grid-cols-3 gap-4" data-testid="ocorrencias-resumo">
        {GRAVIDADES.map((g) => (
          <div key={g.v} className="rounded-3xl border border-slate-200/80 bg-white p-5">
            <div className="text-3xl font-extrabold tracking-tight text-slate-900">{resumo[g.v] ?? 0}</div>
            <div className="mt-1 text-sm font-semibold text-slate-600">{g.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button className={chip(!tipo)} onClick={() => setTipo('')} data-testid="filtro-tipo-todos">Todos os tipos</button>
        {TIPOS.map((t) => (
          <button key={t.v} className={chip(tipo === t.v)} onClick={() => setTipo(t.v)}
            data-testid={`filtro-tipo-${t.v}`}>{t.label}</button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button className={chip(!gravidade)} onClick={() => setGravidade('')} data-testid="filtro-grav-todas">Todas</button>
        {GRAVIDADES.map((g) => (
          <button key={g.v} className={chip(gravidade === g.v)} onClick={() => setGravidade(g.v)}
            data-testid={`filtro-grav-${g.v}`}>{g.label}</button>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          onClick={() => setForm(null)} data-testid="ocorrencias-modal">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="max-h-[88vh] w-full max-w-2xl overflow-auto rounded-3xl bg-white p-7 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between">
              <h2 className="text-xl font-bold tracking-tight text-slate-900">
                {form.id ? 'Editar ocorrência' : 'Nova ocorrência'}
              </h2>
              <button onClick={() => setForm(null)} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>

            {!form.id && (
              <div className="mb-4">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Aluno</span>
                {aluno ? (
                  <div className="flex items-center justify-between rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-2.5">
                    <span className="text-sm font-bold text-violet-800">{aluno.name}</span>
                    <button onClick={() => setAluno(null)} className="text-xs font-bold text-violet-700"
                      data-testid="ocorrencia-trocar-aluno">trocar</button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 py-2.5">
                      <Search className="h-4 w-4 text-slate-400" />
                      <input value={alunoQ} onChange={(e) => buscarAluno(e.target.value)}
                        placeholder="Buscar aluno por nome..."
                        className="w-full bg-transparent text-sm outline-none" data-testid="ocorrencia-busca-aluno" />
                    </div>
                    <div className="mt-1 space-y-1">
                      {alunos.map((a) => (
                        <button key={a.id} onClick={() => { setAluno(a); setAlunos([]); }}
                          className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm hover:bg-violet-50"
                          data-testid={`ocorrencia-aluno-${a.id}`}>
                          <span className="font-semibold text-slate-700">{a.name}</span>
                          <Plus className="h-4 w-4 text-violet-500" />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            <OcorrenciaForm value={form} onChange={setForm} onSubmit={salvar}
              onCancel={() => setForm(null)} saving={saving} />
          </motion.div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando ocorrências...
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-3xl border border-dashed border-slate-200 py-16 text-center text-sm text-slate-400"
          data-testid="ocorrencias-vazio">Nenhuma ocorrência encontrada com esses filtros.</p>
      ) : (
        <div className="space-y-3" data-testid="ocorrencias-lista">
          {items.map((o) => (
            <div key={o.id} onDoubleClick={() => navigate(`/gestao/alunos/${o.student_id}`)}>
              <OcorrenciaItem o={o} podeEditar={podeEditar} onEdit={setForm} onDelete={excluir} showStudent />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
